import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import { Form } from 'reactstrap';

import { Button, DangerAlert, appPropTypes, useLanguageContext } from '../../../../_shared';
import { messages } from '../../../../_shared/i18n/messages';

import { CONTINUATION_TYPES, FORM_TYPES } from '../../../constants';
import { formFieldShape } from '../../../utils';
import useAdvancedSettings from '../hooks/useAdvancedSettings';

import EmbeddedFormSection from './EmbeddedFormSection';

import './EmbeddedFormNavigator.scss';

const propTypes = {

	/**
	 * The form type.
	 */
	type: PropTypes.oneOf([
		FORM_TYPES.APPLICATION,
		FORM_TYPES.FORM,
		FORM_TYPES.POST_REGISTRATION,
	]).isRequired,

	/**
	 * The list of form sections for the form.
	 */
	formSections: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The form section ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The title (DEPRECATED, has been moved to form_section_contents).
		 */
		title: PropTypes.string.isRequired,

		/**
		 * The description (DEPRECATED, has been moved to form_section_contents).
		 */
		description: PropTypes.string,

		/**
		 * The type of navigation to apply to the section.
		 */
		continuation_type: PropTypes.oneOf([
			CONTINUATION_TYPES.SUBMIT,
			CONTINUATION_TYPES.DEFAULT,
			CONTINUATION_TYPES.SECTION,
			CONTINUATION_TYPES.PRODUCT_TICKETS,
			CONTINUATION_TYPES.PRODUCT_TICKET_GROUPS,
		]),

		/**
		 * The ID of the section to navigate to after this section, if any.
		 */
		continuation_section_id: PropTypes.string,

		/**
		 * The list of form fields in the section.
		 */
		fields: PropTypes.arrayOf(formFieldShape).isRequired,

		/**
		 * The multi-language content for the form section.
		 */
		form_section_contents: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * The language.
			 */
			language: PropTypes.string.isRequired,

			/**
			 * The title.
			 */
			title: PropTypes.string.isRequired,

			/**
			 * The description.
			 */
			description: PropTypes.string,

		})).isRequired,

	})).isRequired,

	/**
	 * The current form data.
	 */
	data: PropTypes.objectOf(PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
		PropTypes.bool,
		PropTypes.object,
		PropTypes.arrayOf(PropTypes.string),
	])).isRequired,

	/**
	 * The user's product ticket for the invoice item, used
	 * to check against conditional form navigation settings
	 * (course application forms only).
	 */
	userTicket: PropTypes.shape({

		/**
		 * The product ticket ID.
		 */
		id: PropTypes.string.isRequired,

	}),

	/**
	 * The user's product ticket group for the invoice item, used
	 * to check against conditional form navigation settings
	 * (course application forms only).
	 */
	userTicketGroup: PropTypes.shape({

		/**
		 * The product ticket group ID.
		 */
		id: PropTypes.string.isRequired,

	}),

	/**
	 * The set of validation errors for form fields.
	 */
	errors: PropTypes.objectOf(PropTypes.bool).isRequired,

	/**
	 * True, if the user is the editing the the form.
	 */
	isEditing: PropTypes.bool,

	/**
	 * True, if the user is the previewing the the form.
	 */
	isPreview: PropTypes.bool,

	/**
	 * The settings for filestack.
	 */
	filestack: appPropTypes.filestack,

	/**
	 * Callback to handle changes to form field inputs.
	 */
	onChange: PropTypes.func,

	/**
	 * Callback to cancel the form submission.
	 */
	onCancel: PropTypes.func,

	/**
	 * Callback to validate the form fields for the current section.
	 */
	onValidate: PropTypes.func.isRequired,

	/**
	 * Callback to save the form.
	 */
	onSave: PropTypes.func.isRequired,

};

/**
 * Displays the current form section and determines navigational
 * logic for the form given the form section and form field option conditional
 * settings.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object}  The component to render.
 */
const EmbeddedFormNavigator = (props) => {

	const {
		type,
		formSections,
		data,
		userTicket,
		userTicketGroup,
		errors,
		isEditing,
		isPreview,
		filestack,
		onChange,
		onCancel,
		onValidate,
		onSave,
	} = props;

	const intl = useIntl();

	const { chooseContentFromLanguage } = useLanguageContext();

	const formRef = useRef();

	// Keep track of the navigational history for the section index
	const [indexHistory, setIndexHistory] = useState([0]);

	const currentSectionIdx = indexHistory[indexHistory.length - 1];
	const currentSection = formSections[currentSectionIdx];

	const formProgress = Math.floor(((currentSectionIdx + 1) / formSections.length) * 100);

	const hasPrevious = currentSectionIdx > 0;
	const hasNext = currentSectionIdx < (formSections.length - 1);

	// Keep track of the advanced settings for the current section
	const advancedSettings = useAdvancedSettings(currentSection, { isPreview });

	/**
	 * Scroll user to top of form when user navigates to the next or
	 * previous page.
	 */
	useEffect(() => {

		if (formRef && formRef.current) {

			if (type === FORM_TYPES.APPLICATION) {

				window.scrollTo(0, 0);

			} else {

				formRef.current.scrollIntoView();

			}

		}

	}, [indexHistory, type]);

	/**
	 * Determines the proceeding section based on the conditional settings
	 * for the current form section and form field options.
	 *
	 * The conditional settings on the form field option with the highest weight
	 * will be respected. If there are no conditional form fields, or no option
	 * is selected for a conditional form field, the conditional settings on the
	 * form section will be respected.
	 *
	 * @returns {Number} The index of the next section.
	 */
	const getNextSection = () => {

		const nextIndexDefault = Math.min(formSections.length, currentSectionIdx + 1);

		// Check if current section has advanced navigation set
		let activeAdvancedSetting;

		if (userTicket && currentSection.continuation_type === CONTINUATION_TYPES.PRODUCT_TICKETS) {
			activeAdvancedSetting = advancedSettings.data.find((advancedSetting) => {
				return advancedSetting.object_type === CONTINUATION_TYPES.PRODUCT_TICKETS &&
					advancedSetting.object_id === userTicket.id;
			});
		}

		if (userTicketGroup &&
			currentSection.continuation_type === CONTINUATION_TYPES.PRODUCT_TICKET_GROUPS) {
			activeAdvancedSetting = advancedSettings.data.find((advancedSetting) => {
				return advancedSetting.object_type === CONTINUATION_TYPES.PRODUCT_TICKET_GROUPS &&
					advancedSetting.object_id === userTicketGroup.id;
			});
		}

		const nextSectionIdxAdvanced =
			getNextIndexFromFormItem(activeAdvancedSetting, formSections, nextIndexDefault);

		// Advanced settings take precedence over all other settings
		if (nextSectionIdxAdvanced >= 0) {
			return nextSectionIdxAdvanced;
		}

		// Check if individual field(s) have conditional navigation set
		let nextSectionIdxField = -1;

		const conditionalFields = currentSection.fields.filter((field) => field.conditional);

		conditionalFields.forEach((field) => {

			const selectedOptionID = data[`${field.id}:selectedOptionID`];

			if (selectedOptionID) {

				// Find the corresponding field option
				const fieldOption = field.form_field_options.find(
					(option) => option.id === selectedOptionID
				);

				nextSectionIdxField = getNextIndexFromFormItem(fieldOption, formSections, nextIndexDefault);

			}

		});

		// The last form field option takes precedence over section setting
		if (nextSectionIdxField >= 0) {
			return nextSectionIdxField;
		}

		// Check if overall section has conditional navigation set
		const nextSectionIdxSection =
			getNextIndexFromFormItem(currentSection, formSections, nextIndexDefault);

		if (nextSectionIdxSection >= 0) {
			return nextSectionIdxSection;
		}

		// Otherwise return the next section determined by weight
		return nextIndexDefault;

	};

	/**
	 * Updates the state to return to the previous section.
	 *
	 * The preceding section is determined by the navigational
	 * history of the form, which may vary depending on form
	 * section and/or form field option condition settings.
	 */
	const onBack = () => {

		setIndexHistory((prev) => {

			const result = [...prev];

			result.pop();

			return result;

		});

	};

	/**
	 * Validates the form entry data for the current form section
	 * and updates the state to move to the next form section.
	 */
	const onNext = () => {

		onValidate(currentSection.id, () => {

			setIndexHistory((prev) => (
				[...prev, getNextSection()]
			));

		});

	};

	/**
	 * Handles submitting the form data. Only fields for the
	 * current section will be validated before submission. If the
	 * current section is the submit prompt placeholder, no fields
	 * will be validated before submission.
	 *
	 * @param {Object} event  The form submission event.
	 */
	const onSubmit = (event) => {

		onSave(event, {
			// Only validate fields for the current section
			formSectionID: currentSection?.id,
			// Ignore validation for submit prompt placeholder section since we will have
			// already validated the preceding sections
			bypassValidation: currentSectionIdx === formSections.length,
		});

	};

	// Check if the form entry for the current section has errors (i.e. at least one)
	const hasErrors = currentSection?.fields.reduce((acc, field) => {
		return acc || errors[field.id];
	}, false);

	if (currentSection && advancedSettings.loading) {
		return (
			<div className="loading-spinner">
				<i className="fas fa-circle-notch fa-spin" />
			</div>
		);
	}

	// If unable to loading settings, don't show form!
	if (advancedSettings.error) {
		return (
			<DangerAlert>
				<FormattedMessage
					{...messages.TC_COMPONENTS_EMBEDDED_FORM_MSG_TEXT_LOAD_ERROR} />
			</DangerAlert>
		);
	}

	// Get the translated content for the default form section, if any
	const defaultFormSectionContent = chooseContentFromLanguage(
		formSections?.[0]?.form_section_contents
	);

	return (
		<Form
			innerRef={formRef}
			className="form-navigator"
			onSubmit={onSubmit}>
			<h1>
				{
					defaultFormSectionContent?.title ||
					intl.formatMessage(messages.TC_COMPONENTS_FORM_TITLE_DEFAULT)
				}
			</h1>
			{defaultFormSectionContent?.description &&
				<div className="text-muted mb-3">
					{defaultFormSectionContent.description}
				</div>
			}
			{/* Display the current form section */}
			{currentSection &&
				<EmbeddedFormSection
					index={currentSectionIdx}
					formSection={currentSection}
					data={data}
					errors={errors}
					isEditing={isEditing}
					isPreview={isPreview}
					filestack={filestack}
					onChange={onChange} />
			}
			{/* If the user has jumped to form submission, show a final submission prompt message */}
			{currentSectionIdx === formSections.length &&
				<div className="mb-3">
					<FormattedMessage
						{...messages.TC_COMPONENTS_FORM_EMPTY_SECTION_SUBMIT_PROMPT} />
				</div>
			}
			<div className="form-nav-btns d-flex">
				{hasPrevious &&
					<Button
						color="neutral"
						type="button"
						outline
						onClick={onBack}>
						<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_BACK_LABEL} />
					</Button>
				}
				{hasNext &&
					<Button
						className="ms-auto"
						color="neutral"
						type="button"
						disabled={hasErrors}
						outline
						onClick={onNext}>
						<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_NEXT_LABEL} />
					</Button>
				}
				{!hasPrevious && onCancel &&
					<Button
						className="ms-3"
						color="neutral"
						type="button"
						outline
						onClick={onCancel}>
						<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_CANCEL_LABEL} />
					</Button>
				}
				{!hasNext && isEditing &&
					<Button
						className="ms-auto my-0"
						color="primary"
						disabled={hasErrors}
						type={isPreview ? 'button' : 'submit'}>
						<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_SUBMIT_LABEL} />
					</Button>
				}
			</div>
			<div
				className="form-progress-bar-container">
				<div
					className="form-progress-bar"
					style={{ width: `${formProgress}%` }} />
			</div>
		</Form>
	);

};

EmbeddedFormNavigator.propTypes = propTypes;

export default EmbeddedFormNavigator;

/**
 * Get the index of the proceeding section based on the navigational
 * settings for the given form item (section, field option, or advanced setting).
 *
 * @param {Object} formItem          The form item to process.
 * @param {Array}  formSections      The list of form sections.
 * @param {Number} nextIndexDefault  The index of the default proceeding section.
 *
 * @returns {Number}  The index of the proceeding section, or -1 if the form item
 *                    navigation settings should not be respected.
 */
function getNextIndexFromFormItem(formItem, formSections, nextIndexDefault) {

	if (!formItem) {
		return -1;
	}

	if (formItem.continuation_type === CONTINUATION_TYPES.SECTION) {

		return formSections.findIndex((section) =>
			section.id === formItem.continuation_section_id);

	} else if (formItem.continuation_type === CONTINUATION_TYPES.SUBMIT) {

		return formSections.length;

	} else if (formItem.continuation_type === CONTINUATION_TYPES.DEFAULT) {

		return nextIndexDefault;

	}

	// Otherwise invalid continuation type, ignore setting
	return -1;

}
